import React, { useRef } from "react";
import { computeDueAsOfMonth, computeStudentFinance, parseMoneyAmount } from "../utils/feeCalculator";
import "./StatementOfAccount.css";

export default function StatementOfAccount({ student, payments = [], onClose }) {
  const printRef = useRef();
  const currentDate = new Date().toISOString().split("T")[0];

  // Filter payments for this student and calculate totals
  const studentPayments = Array.isArray(payments) ? 
    payments.filter(p => p.studentId === student.id) : [];
  
  const campusKey = String(student?.campus || "").toLowerCase();
  const normalizedCampus = campusKey.includes("bulacan") ? "bulacan" : (campusKey.includes("gapan") ? "gapan" : campusKey);
  const paymentMode = student?.paymentMode || "Annual";
  const fin = computeStudentFinance({ ...(student || {}), campus: normalizedCampus, paymentMode }, studentPayments, "Annual");
  const totalDue = Number(fin.totalDue || 0);
  const totalPaid = studentPayments.reduce((sum, p) => sum + parseMoneyAmount(p.amount), 0);
  const outstanding = Math.max(0, totalDue - totalPaid);
  const targetMonth = String(paymentMode || "").toLowerCase() === "installment" ? "July" : "";
  const startYear = Number(student?.enrollmentYear);
  const dueInfo = targetMonth ? computeDueAsOfMonth(fin.schedule || [], studentPayments, targetMonth, { startMonth: "July", startYear: Number.isFinite(startYear) ? startYear : undefined }) : null;
  const totalAmountDue = Number(dueInfo?.dueAmount || 0);
  const dueDate = String(dueInfo?.dueDate || "");
  const preparedBy = normalizedCampus === "bulacan" ? "Prepared by: Emerenciana L. Ventura" : "Prepared by: Catalina T. Basa";

  // Group payments by month for better organization
  const paymentsByMonth = studentPayments.reduce((acc, payment) => {
    const month = payment.date.substring(0, 7); // Get YYYY-MM
    if (!acc[month]) acc[month] = [];
    acc[month].push(payment);
    return acc;
  }, {});

  // Sort months chronologically
  const sortedMonths = Object.keys(paymentsByMonth).sort();

  // Print handler with enhanced styling
  const handlePrint = () => {
    const content = printRef.current;
    const printWindow = window.open("", "_blank");
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Statement of Account - ${student?.fullname || student?.name}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .school-name { font-size: 24px; font-weight: bold; }
            .statement-title { font-size: 20px; margin-top: 10px; }
            .student-info { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
            th { background-color: #f2f2f2; }
            .total-row { font-weight: bold; }
            .footer { margin-top: 50px; font-size: 12px; text-align: center; }
            @media print {
              body { padding: 0; }
              button { display: none; }
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
          <div class="footer">
            <p>This is an official statement of account as of ${currentDate}.</p>
            <p>For questions or concerns, please contact the school finance office.</p>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  // Export to CSV
  const exportCSV = () => {
    // Prepare CSV content
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Date,Description,Reference,Method,Amount\n";
    
    studentPayments.forEach(payment => {
      csvContent += `${payment.date},Payment,${payment.reference},${payment.method},${payment.amount}\n`;
    });
    
    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `statement_${student?.id || "student"}_${currentDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Email handler (basic: opens mail client with statement in body)
  const handleEmail = () => {
    const subject = `Statement of Account for ${student?.fullname || student?.name}`;
    const body = `
Statement of Account for ${student?.fullname || student?.name}
Total Due: ₱${totalDue.toLocaleString()}
Total Paid: ₱${totalPaid.toLocaleString()}
Outstanding: ₱${outstanding.toLocaleString()}

Payments:
${studentPayments.map(p => `${p.date}: ₱${p.amount} (${p.method}) Ref: ${p.reference}`).join("\n")}
  `;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="card p-3 soa-print">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Statement of Account</h5>
        <div>
          <button className="btn btn-sm btn-outline-primary me-2" onClick={handlePrint}>
            <i className="bi bi-printer"></i> Print
          </button>
          <button className="btn btn-sm btn-outline-success me-2" onClick={exportCSV}>
            <i className="bi bi-file-earmark-excel"></i> Export CSV
          </button>
          <button className="btn btn-sm btn-outline-info me-2" onClick={handleEmail}>
            <i className="bi bi-envelope"></i> Email
          </button>
          {onClose && (
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
              <i className="bi bi-x"></i> Close
            </button>
          )}
        </div>
      </div>

      <div ref={printRef} className="bg-white p-4">
        <div className="text-center mb-4">
           <h4 className="fw-bold">VMS School Portal System</h4>
           <h5 className="mt-2">STATEMENT OF ACCOUNT</h5>
           <p>As of {currentDate}</p>
         </div>

        <div className="student-info mb-4">
          <p><strong>Student:</strong> {student?.fullname || student?.name || "N/A"}</p>
          <p><strong>ID:</strong> {student?.id || "N/A"}</p>
          <p><strong>Grade Level:</strong> {student?.gradeLevel || student?.level || "N/A"}</p>
          <p><strong>Campus:</strong> {student?.campus || "N/A"}</p>
        </div>

        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h6>Total Due</h6>
                <h4>₱{totalDue.toLocaleString()}</h4>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h6>Total Paid</h6>
                <h4>₱{totalPaid.toLocaleString()}</h4>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h6>Outstanding Balance</h6>
                <h4 className="text-primary">
                  ₱{outstanding.toLocaleString()}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h6>Total Amount Due</h6>
                <h4 className="text-danger">₱{totalAmountDue.toLocaleString()}</h4>
              </div>
            </div>
          </div>
        </div>
        <div className="text-center mb-4">
          <div style={{ fontWeight: 700 }}>Due Date: {dueDate || "N/A"}</div>
        </div>

        <p className="mb-4 fst-italic text-center">
          Kindly settle the following account on or before the assigned date.
        </p>

        <h5>Payment History</h5>
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Reference</th>
              <th>Method</th>
              <th className="text-end">Amount</th>
            </tr>
          </thead>
          <tbody>
            {sortedMonths.map(month => (
              <React.Fragment key={month}>
                <tr className="table-secondary">
                  <td colSpan="5"><strong>{new Date(month + "-01").toLocaleDateString("en-US", { year: "numeric", month: "long" })}</strong></td>
                </tr>
                {paymentsByMonth[month].map((payment, index) => (
                  <tr key={payment.reference || index} className="payment-row">
                    <td>{payment.date}</td>
                    <td>Payment</td>
                    <td>{payment.reference}</td>
                    <td>{payment.method}</td>
                    <td className="text-end">₱{payment.amount.toLocaleString()}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {studentPayments.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center text-muted">No payment records found</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="table-light">
              <td colSpan="4" className="text-end"><strong>Total Payments:</strong></td>
              <td className="text-end">₱{totalPaid.toLocaleString()}</td>
            </tr>
          </tfoot>
        </table>
        <div className="mt-4 text-end">
          <div style={{ fontWeight: 700 }}>{preparedBy}</div>
        </div>
      </div>
    </div>
  );
}
